import { LmText } from '@mes-ui/lemma';
import { Box, Stack, useMediaQuery } from '@mui/material';
import { WidgetsData } from 'api/types/widgets';
import ResultDoughnut from 'components/common/ResultDoughnut';
import { DiagnosticLevelColorSet1, GiaType } from 'const';
import { accentColors } from 'portfolio3/styles';
import { commonTheme } from 'portfolio3/styles/theme';
import {
  getDiagnosticLevelTypeColor,
  getDiagnosticMarkValue5Color,
  getDiagnosticPercentColor,
  getExamDoughnutData,
  isDefined,
} from 'utils';

type PointsContentProps = {
  widgetData: WidgetsData[number];
};

const noPointsFormCode = [GiaType.writing, GiaType.narration, GiaType.interviewRus];

const PointsContent = ({ widgetData }: PointsContentProps) => {
  const isMobile = useMediaQuery(commonTheme.breakpoints.down('commonSm'));
  const CONTENT_HEIGHT = isMobile ? 66 : 80;

  const getSubjectName = () => {
    switch (widgetData.blockName) {
      case 'INDEPENDENT_DIAGNOSIC':
      case 'INDIVIDUAL_DIAGNOSIC':
        return widgetData.data.selfDiagnosticSubjectName;
      case 'GIA_EXAM':
        return widgetData.data.subjectName;
      default:
        return '';
    }
  };

  const renderResults = () => {
    switch (widgetData.blockName) {
      case 'INDEPENDENT_DIAGNOSIC': {
        const result = widgetData.data.selfDiagnosticExecutionResultMarkValue;
        const maxResult = widgetData.data.selfDiagnosticMaxExecutionResult;
        return `${result} баллов из ${maxResult}`;
      }
      case 'INDIVIDUAL_DIAGNOSIC': {
        const result = widgetData.data.selfDiagnosticExecutionResultMarkValue;
        return `${result} баллов`;
      }
      case 'GIA_EXAM': {
        if (noPointsFormCode.includes(widgetData.data.examFormCode)) {
          return '';
        }
        return `${widgetData.data.primaryMarkValue} баллов из ${widgetData.data.primaryMarkBasis}`;
      }
      default:
        return '';
    }
  };

  const getDoughnutValues = () => {
    switch (widgetData.blockName) {
      case 'INDEPENDENT_DIAGNOSIC': {
        const percentValue = widgetData.data.selfDiagnosticPercentExecutionResult;
        const markValue5 = widgetData.data.markValue5;
        const hasMarkValue = isDefined(markValue5);
        const resultChartColor = hasMarkValue
          ? getDiagnosticMarkValue5Color(markValue5, DiagnosticLevelColorSet1, '80')
          : getDiagnosticLevelTypeColor(widgetData.data.levelType ?? '', DiagnosticLevelColorSet1, '80');

        return {
          doughnutData: {
            incomingData: [percentValue, 100 - percentValue],
            incomingLabels: ['1', '2'],
          },
          doughnutColor: resultChartColor,
          doughnutTotal: percentValue,
        };
      }
      case 'INDIVIDUAL_DIAGNOSIC': {
        const percentValue = widgetData.data.selfDiagnosticPercentExecutionResult;

        return {
          doughnutData: {
            incomingData: [percentValue, 100 - percentValue],
            incomingLabels: ['1', '2'],
          },
          doughnutColor: getDiagnosticPercentColor(percentValue, [
            accentColors.red[80],
            accentColors.orange[80],
            accentColors.yellow[80],
            accentColors.green[80],
          ]),
          doughnutTotal: percentValue,
        };
      }
      case 'GIA_EXAM': {
        const res = getExamDoughnutData({
          formaGia: widgetData.data.examFormCode,
          approbation: widgetData.data.approbation,
          normalizedMarkValue: widgetData.data.normalizedMarkValue,
          normalizedMarkBasis: widgetData.data.normalizedMarkBasis,
          percentMarkValue: widgetData.data.percentMarkValue,
          positiveResultThreshold: widgetData.data.positiveResultThreshold,
          isCredit: widgetData.data.isCredit,
        });
        return res;
      }
      default:
        return null;
    }
  };

  const doughnutValues = getDoughnutValues();

  return (
    <Stack spacing={1.5} direction="row">
      <Box
        sx={{
          width: CONTENT_HEIGHT,
          height: CONTENT_HEIGHT,
          borderRadius: '22px',
          position: 'relative',
        }}
      >
        {doughnutValues && (
          <ResultDoughnut
            total={doughnutValues.doughnutTotal ?? '-'}
            data={doughnutValues.doughnutData}
            color={doughnutValues.doughnutColor}
          />
        )}
      </Box>
      <Stack direction="column" spacing={0.5} style={{ minWidth: 0 }}>
        <LmText variant="lg" weight="medium">
          Твой результат
        </LmText>
        <LmText ellipsis variant="lg">
          {getSubjectName()}
        </LmText>
        <LmText variant="sm" color="note">
          {renderResults()}
        </LmText>
      </Stack>
    </Stack>
  );
};

export default PointsContent;
